import { Component } from 'react';
import Isvg from 'react-inlinesvg';
import { API_ENDPOINT } from '../../constants';
import ListBuilder from '../../components/listBuilder';
import Page from '../../containers/page';
import { updateParamsHelper, getParamsFromURL } from '../../helpers/custom.helper';
import { Container, Row, Col, Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import view from '../../assets/svg/eye.svg';
import deleteIcon from '../../assets/svg/garbage.svg';

const RETRIES_DISPLAY_CAP = 10;

class JournalFailedPatientSyncLogs extends Component {
    constructor(props) {
        super(props);
        this.state = {
            total: 0,
            params: {
                entries: 10,
                page: 0,
                sortField: '_id',
                sortType: -1,
                filter: '',
            },
            loading: true,
            initialList: [],
            error: null,
            errorHistoryModal: null,
        };
    }

    get = async () => {
        this.setState({ loading: true, initialList: [] });

        try {
            const response = await fetch(`${API_ENDPOINT}/journal/failed-patient-sync/get`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
                },
                body: JSON.stringify({
                    groupId: this.props.selectedGroup,
                    groupAlias: localStorage.getItem('groupAlias'),
                    total: this.state.total,
                    entries: this.state.params?.entries,
                    page: this.state.params?.page,
                    sortField: this.state.params?.sortField,
                    sortType: this.state.params?.sortType,
                    filter: this.state.params?.filter,
                }),
            });

            const data = await response.json();

            if (data.items) {
                this.setState({ loading: false, initialList: data.items, total: data.total });
            } else {
                this.setState({ loading: false, error: data.error || null });
            }
        } catch (err) {
            this.setState({ loading: false, error: err?.message || 'Error fetching failed patient sync logs' });
        }
    };

    delete = async (id) => {
        this.setState({ loading: true });
        try {
            const groupId = encodeURIComponent(this.props.selectedGroup || '');
            const groupAlias = encodeURIComponent(localStorage.getItem('groupAlias') || '');
            await fetch(`${API_ENDPOINT}/journal/failed-patient-sync/${id}?groupId=${groupId}&groupAlias=${groupAlias}`, {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
                },
            });
            await this.get();
        } catch (err) {
            this.setState({ loading: false, error: err?.message || 'Error deleting failed patient sync log' });
        }
    }

    componentDidMount() {
        const urlParams = getParamsFromURL();

        const newParams = {
            ...this.state.params,
            entries: urlParams.entries,
            filter: urlParams.filter,
        };

        this.setState({ params: newParams }, () => {
            if (this.props.initialList) {
                this.setState({ loading: false, initialList: this.props.initialList });
            } else {
                this.get();
            }
        });
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps.selectedGroup !== this.props.selectedGroup ||
            prevProps.view !== this.props.view
        ) {
            this.get();
        }
    }

    updateSort = (field, type) => {
        updateParamsHelper(this, { sortField: field, sortType: type });
    };

    updateParams = (name, value, restart = false) => {
        updateParamsHelper(this, { [name]: value }, restart);
    };

    updateMultipleParams = (fields, restart = false) => {
        updateParamsHelper(this, fields, restart);
    };

    formatTs = (ts) => {
        if (!ts) return '-';
        return new Date(ts).toLocaleString('sv-SE');
    };

    translateError = (error) => String(error || '')
        .split('; ')
        .map(part => part.translate(this.props.lang))
        .join('; ');

    openErrorHistory = (item) => {
        this.setState({ errorHistoryModal: item });
    };

    closeErrorHistory = () => {
        this.setState({ errorHistoryModal: null });
    };


    render() {
        const { params, initialList, loading, total, errorHistoryModal } = this.state;

        return (
            <div className={this.props.autoHideSidebar ? 'dashboard dashboard-auto-hide' : 'dashboard'}>
                <Container fluid>
                    <Row>
                        <Col lg='12'>
                            <div className='panel aeb-panel'>
                                <div className='panel-header'>
                                    <h5 className='component-header'>
                                        {'Failed patient sync'.translate(this.props.lang)}
                                    </h5>
                                </div>

                                <ListBuilder
                                    lang={this.props.lang}
                                    loading={loading}
                                    total={total}
                                    hideEntries={false}
                                    showNumeration={false}
                                    fields={[
                                        { type: 'text', label: 'Patient'.translate(this.props.lang), name: 'patientName' },
                                        { type: 'text', label: 'Social security number'.translate(this.props.lang), name: 'socialSecurityNumber' },
                                        { type: 'text', label: 'Email'.translate(this.props.lang), name: 'patientEmail' },
                                        { type: 'text', label: 'Error'.translate(this.props.lang), name: 'errorMessage' },
                                        { type: 'text', label: 'Retries'.translate(this.props.lang), name: 'retriesFormatted' },
                                        { type: 'text', label: 'Last attempt'.translate(this.props.lang), name: 'lastAttemptFormatted' },
                                        { type: 'text', label: 'Created'.translate(this.props.lang), name: 'createdFormatted' },
                                    ]}
                                    items={initialList.map((item) => {
                                        const retries = item.retries ?? 0;
                                        const error = this.translateError(item.error);

                                        return {
                                            ...item,
                                            errorMessage: error
                                                ? <strong className='journal-sync-error-message' title={error}>{error.length > 60 ? error.slice(0, 60) + '...' : error}</strong>
                                                : '-',
                                            retriesFormatted: retries > RETRIES_DISPLAY_CAP ? `${RETRIES_DISPLAY_CAP}+` : String(retries),
                                            lastAttemptFormatted: this.formatTs(item.lastAttemptAt),
                                            createdFormatted: this.formatTs(item.createdAt),
                                        };
                                    })}
                                    actions={[
                                        {
                                            component: <Isvg src={view} className='view-icon' />,
                                            onClick: (item) => {
                                                if (item.errorHistory?.length) {
                                                    this.openErrorHistory(item);
                                                }
                                            },
                                        },
                                        {
                                            component: <Isvg src={deleteIcon} className='delete-icon' />,
                                            onClick: (item) => this.delete(item._id),
                                        },
                                    ]}
                                    rawItems={this.state.initialList}
                                    params={params}
                                    sortField={params.sortField}
                                    sortType={params.sortType}
                                    updateSort={this.updateSort}
                                    updateParams={this.updateParams}
                                    updateMultipleParams={this.updateMultipleParams}
                                />
                            </div>
                        </Col>
                    </Row>
                </Container>

                {errorHistoryModal && (
                    <Modal isOpen centered toggle={this.closeErrorHistory} size='lg'>
                        <ModalHeader toggle={this.closeErrorHistory}>
                            {'Error History'.translate(this.props.lang)}
                        </ModalHeader>
                        <ModalBody className='journal-sync-modal-body'>
                            {errorHistoryModal.errorHistory.length === 0 ? (
                                <p className='journal-sync-modal-empty'>{'No errors recorded.'.translate(this.props.lang)}</p>
                            ) : (
                                errorHistoryModal.errorHistory.map((entry, i) => (
                                    <div key={i} className='journal-sync-modal-entry'>
                                        <div className='journal-sync-modal-entry-header'>
                                            <strong className='journal-sync-modal-entry-num'>#{i + 1}</strong>
                                            <span className='journal-sync-modal-entry-ts'>
                                                {this.formatTs(entry.firstSeenAt)}
                                                {entry.lastSeenAt && entry.lastSeenAt !== entry.firstSeenAt ? ` - ${this.formatTs(entry.lastSeenAt)}` : ''}
                                                {entry.count > RETRIES_DISPLAY_CAP
                                                    ? ` (${RETRIES_DISPLAY_CAP}+ ${'retries'.translate(this.props.lang)})`
                                                    : entry.count > 1
                                                        ? ` (${entry.count}x)`
                                                        : ''}
                                            </span>
                                        </div>
                                        <div className='journal-sync-modal-section'>
                                            <strong className='journal-sync-modal-section-title'>
                                                {'Error'.translate(this.props.lang)}{entry.status ? ` (HTTP ${entry.status})` : ''}
                                            </strong>
                                            <pre className='journal-sync-modal-pre'>{this.translateError(entry.error)}</pre>
                                        </div>
                                        {entry.errorFields && (
                                            <div className='journal-sync-modal-section'>
                                                <strong className='journal-sync-modal-section-title'>{'Technical details'.translate(this.props.lang)}</strong>
                                                <pre className='journal-sync-modal-pre'>{JSON.stringify(entry.errorFields, null, 2)}</pre>
                                            </div>
                                        )}
                                    </div>
                                ))
                            )}
                        </ModalBody>
                        <ModalFooter>
                            <Button color='secondary' onClick={this.closeErrorHistory}>
                                {'Close'.translate(this.props.lang)}
                            </Button>
                        </ModalFooter>
                    </Modal>
                )}
            </div>
        );
    }
}

export default Page(JournalFailedPatientSyncLogs);
